import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ArrowIcon, CheckIcon } from "@/components/icons";
import { useState } from "react";
import { PASSWORD_REQUIREMENTS } from "@/lib/password";

const STEPS = [
  ["Add your sources", "Upload Markdown, PDF, Word, text or CSV."],
  ["Test it in the workspace", "Ask a real question and check the cited passage."],
  ["Paste one script tag", "The widget appears on your site right away."],
];

export const AuthShell = ({ homeTestId, aside = true, children }) => (
  <div className="min-h-screen text-foreground antialiased selection:bg-accent lg:grid lg:grid-cols-2">
    <div className="px-6 py-8 lg:px-16 lg:py-8 flex flex-col min-h-screen">
      <Logo to="/" testId={homeTestId} />
      <div className="flex-1 flex items-center justify-center py-10">
        <div className="w-full max-w-[420px] flex flex-col gap-6 fade-up">{children}</div>
      </div>
    </div>
    {aside && (
      <div className="paper-dark hidden lg:flex flex-col justify-between text-primary-foreground px-20 py-24">
        <div className="flex flex-col gap-12">
          <h2 className="font-heading text-[60px] leading-[1.02] tracking-[-0.02em] max-w-[520px]">
            Live on your site in <span className="text-accent">about five minutes.</span>
          </h2>
          <div className="flex flex-col gap-7">
            {STEPS.map(([title, sub], i) => (
              <div key={title} className="flex gap-5 items-baseline">
                <span className="font-heading text-4xl text-accent w-10">{i + 1}</span>
                <div>
                  <div className="text-[19px] font-semibold">{title}</div>
                  <div className="text-base leading-normal text-[#D8D4C8] mt-1">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-soft px-6 py-[22px] flex flex-col gap-3 max-w-[480px]">
          <div className="text-base leading-[1.55]">
            Choose <span className="bg-accent text-accent-foreground px-[3px]">Regenerate key</span> under Settings, then API.
          </div>
          <div className="self-start inline-flex items-center gap-2 h-7 px-3 rounded-full border border-primary-foreground font-mono text-xs">
            <span>1</span>
            <span>api-guide.md</span>
          </div>
        </div>
      </div>
    )}
  </div>
);

export const Heading = ({ title, sub }) => (
  <div className="flex flex-col gap-3">
    <h1 className="font-heading text-[44px] lg:text-[56px] leading-none tracking-[-0.02em]">{title}</h1>
    {sub && <p className="text-[17px] leading-normal text-soft">{sub}</p>}
  </div>
);

export const Field = ({ label, id, testid, onChange, type = "text", ...props }) => {
  const [visible, setVisible] = useState(false);
  const isPassword = type === "password";

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-medium">{label}</label>
      <div className="relative">
        <input
          id={id}
          data-testid={testid}
          type={isPassword && visible ? "text" : type}
          onChange={(e) => onChange(e.target.value)}
          className={`h-[52px] px-4 ${isPassword ? "pr-12" : ""} rounded-xl border border-input bg-[#FFFDF8] text-base outline-none focus:border-foreground w-full`}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            aria-label={visible ? "Hide password" : "Show password"}
            aria-pressed={visible}
            onClick={() => setVisible((current) => !current)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-soft hover:text-foreground"
          >
            {visible ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    </div>
  );
};

export const PasswordHint = () => (
  <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-soft" aria-label="Password requirements">
    {PASSWORD_REQUIREMENTS.map((requirement) => <li key={requirement}>{requirement}</li>)}
  </ul>
);

export const ProviderButtons = ({ onSelect }) => (
  <div className="grid grid-cols-2 gap-3">
    {["Google", "Microsoft", "AWS", "GCP"].map((provider) => (
      <button
        key={provider}
        type="button"
        onClick={() => onSelect(provider)}
        className="h-11 rounded-xl border border-input bg-[#FFFDF8] text-sm font-medium hover:border-foreground transition-colors"
      >
        Continue with {provider}
      </button>
    ))}
  </div>
);

export const SubmitButton = ({ testid, loading, children, arrow = true }) => (
  <button
    data-testid={testid}
    type="submit"
    disabled={loading}
    className="paper-dark h-[54px] rounded-[14px] text-primary-foreground text-[17px] font-medium flex items-center justify-center gap-2.5 disabled:opacity-60"
  >
    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
    {children}
    {arrow && !loading && <ArrowIcon />}
  </button>
);

export const ErrorNote = ({ testid, children }) => (
  <p data-testid={testid} className="text-sm text-destructive bg-destructive/10 border border-destructive/40 rounded-lg px-3 py-2">
    {children}
  </p>
);

export const DoneBadge = () => (
  <div className="w-12 h-12 rounded-full bg-success/10 border border-success/30 text-success flex items-center justify-center">
    <CheckIcon size={22} />
  </div>
);
